import { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import { useOverlayScrollbars } from 'overlayscrollbars-react';
import 'overlayscrollbars/styles/overlayscrollbars.css';
import '../../../styles/kadarOverlayScrollbars.css';

const useStyles = makeStyles(() => ({
  root: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'visible',
  },
  overlayWrapper: {
    boxSizing: 'border-box',
    width: '100%',
    height: '100%',
  },
  scrollableViewport: {
    pointerEvents: 'auto',
    scrollbarGutter: 'stable',
    height: '100%',
    width: '100%',
  },
  verticalSlot: {
    position: 'absolute',
    right: 0,
    height: '100%',
  },
  horizontaSlot: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
  },
}));

const Scrollbars = ({
  className = undefined,
  overlayWrapperClass = undefined,
  viewportClass = undefined,
  verticalScrollClass = undefined,
  horizontalScrollClass = undefined,
  overlayProps = {
    options: {},
    events: {},
    defer: false,
  },
  children,
}) => {
  const classes = useStyles();

  const rootRef = useRef(null);
  const viewportRef = useRef(null);
  const vScrollbarSlotRef = useRef(null);
  const hScrollbarSlotRef = useRef(null);

  const [initialize] = useOverlayScrollbars({
    options: {
      defer: overlayProps.defer,
      overflow: {
        x: 'hidden',
        y: 'scroll',
      },
      scrollbars: {
        theme: 'os-theme-kadar',
        dragScroll: true,
      },
      ...overlayProps.options,
    },
    events: {
      initialized: osInstance => {
        const { scrollbarHorizontal, scrollbarVertical } =
          osInstance.elements();

        if (vScrollbarSlotRef.current && scrollbarVertical) {
          vScrollbarSlotRef.current.append(scrollbarVertical.scrollbar);
        }
        if (hScrollbarSlotRef.current && scrollbarHorizontal) {
          hScrollbarSlotRef.current.append(scrollbarHorizontal.scrollbar);
        }
      },
      ...overlayProps.events,
    },
  });

  useEffect(() => {
    const { current: root } = rootRef;
    const { current: viewport } = viewportRef;

    if (root && viewport) {
      initialize({
        target: root,
        elements: {
          viewport,
        },
      });
    }
  }, [initialize]);

  return (
    <div className={classNames(classes.root, className)}>
      <div
        data-overlayscrollbars-initialize=""
        ref={rootRef}
        className={classNames(classes.overlayWrapper, overlayWrapperClass)}
      >
        <div ref={viewportRef} className={viewportClass}>
          {children}
        </div>
      </div>
      <div
        ref={vScrollbarSlotRef}
        className={classNames(classes.verticalSlot, verticalScrollClass)}
      />
      <div
        ref={hScrollbarSlotRef}
        className={classNames(classes.horizontaSlot, horizontalScrollClass)}
      />
    </div>
  );
};

Scrollbars.propTypes = {
  className: PropTypes.string,
  overlayWrapperClass: PropTypes.string,
  viewportClass: PropTypes.string,
  verticalScrollClass: PropTypes.string,
  horizontalScrollClass: PropTypes.string,
  overlayProps: PropTypes.object,
  children: PropTypes.node.isRequired,
};

export default Scrollbars;
